/**
 * LumiFAB — Global "Ask Lumi" floating pill + modal (Iter 402.3 · Aug 2026)
 *
 * Karine's ask: "Add a small Ask Lumi pill to the site header so clients
 * can pull her up from any page, not just Shop."
 *
 * Renders a fixed-position pill on every page (except /shop where the box
 * is already inline, and /admin, /staff, /kiosk to avoid intrusion). When
 * tapped it opens a full-height modal containing the same SkinAdvisorBox.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import SkinAdvisorBox from "@/components/shop/SkinAdvisorBox";

// Route prefixes where the pill should NOT appear.
const HIDE_ON = ["/shop", "/admin", "/staff", "/kiosk", "/lobby", "/signage", "/studio", "/tv"];

export default function LumiFAB() {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);

  // Global event listener so ANY component (mobile bottom-nav "Ask" tab,
  // Nav "Ask Lumi" link, etc.) can pop Lumi open. Karine ask Feb 2026:
  // "The Lumi skin care bot is hidden on mobile — place it where it will
  // be seen." Rather than crowd the mobile UI with a floating pill on top
  // of the bottom-nav, we wire the existing bottom-nav "Ask" button here.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-lumi", onOpen);
    // Legacy alias — the mobile bottom-nav historically fired
    // 'open-mobile-chat' for the retired Karine chat widget. Lumi now
    // inherits that event so guests see the new AI without any nav rewire.
    window.addEventListener("open-mobile-chat", onOpen);
    // Feb 28 2026 (Karine): kiosk "Ask Lumi" tile deep-links via
    // `?lumi=1`. Auto-open Lumi if that flag is present on mount.
    try {
      if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("lumi") === "1") {
        setTimeout(() => setOpen(true), 400);
      }
    } catch { /* noop */ }
    return () => {
      window.removeEventListener("open-lumi", onOpen);
      window.removeEventListener("open-mobile-chat", onOpen);
    };
  }, []);

  // ESC closes modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const hidden = HIDE_ON.some((p) => pathname === p || pathname.startsWith(p + "/"));
  if (hidden) return null;

  const isFr = (language || "").toLowerCase().startsWith("fr");
  const pillLabel = isFr ? "Parler à Lumi" : "Ask Lumi";
  const modalTitle = isFr ? "Lumi — Votre conseillère beauté IA" : "Lumi — AI Skincare Advisor";

  // Homepage has its own desktop-only sticky right column (book/call CTAs)
  // + StickyBookButton at bottom-8 right-8. To avoid stacking on those, we
  // lift Lumi higher on the desktop homepage only. On mobile & every other
  // page, the bottom-right corner is otherwise empty so we sit at the
  // standard 24px offset.
  const isHome = pathname === "/" || pathname === "";
  const posClasses = isHome
    ? "bottom-4 right-4 md:bottom-32 md:right-8"
    : "bottom-4 right-4 sm:bottom-6 sm:right-6";

  return (
    <>
  {/* Floating pill — MOBILE ONLY as of Feb 28 2026.

      Previously we rendered a bottom-right desktop pill here, but it
      was overlapping the homepage's right-column rotating tile
      (GLP-1 / AI Future / Andropause etc). Karine ask: "put the Lumi
      pill in rotation with the Earn $50 pill every 5 seconds".

      Implementation: the desktop pill lives now in `FloatingReferral`
      (bottom-LEFT column), which alternates between "Refer & Earn $50"
      and "Ask Lumi" every 5 s. On mobile we keep the bottom-nav "Ask"
      button which fires `open-lumi` — no floating pill required. */}
      {/* (desktop pill intentionally removed — see FloatingReferral) */}

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[10002] flex items-start justify-center p-2 sm:px-8 sm:pt-10 sm:pb-8 bg-slate-950/70 backdrop-blur-sm animate-in fade-in duration-200 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          data-testid="lumi-fab-modal"
          role="dialog"
          aria-label={modalTitle}
          aria-modal="true"
        >
          <div className="relative w-full max-w-5xl rounded-3xl shadow-2xl bg-transparent my-auto">
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="lumi-fab-close"
              className="absolute top-3 right-3 z-[10003] w-10 h-10 rounded-full bg-white/95 hover:bg-white text-slate-800 shadow-lg border border-slate-200 flex items-center justify-center transition"
              aria-label={isFr ? "Fermer" : "Close"}
            >
              <X className="w-5 h-5" />
            </button>
            <SkinAdvisorBox language={language || "en"} />
          </div>
        </div>
      )}
    </>
  );
}
